import React, { useEffect, useRef, useState } from 'react';
import {
  FaBalanceScale,
  FaChartLine,
  FaCheckCircle,
  FaComments,
  FaHeart,
  FaRegClock,
  FaUserFriends
} from 'react-icons/fa';
import Onboarding from './Onboarding.jsx';
import Button from '../ui/Button.jsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card.jsx';
import { scrollElementToTop } from '../utils/scroll.js';
import SiteFooterLinks from './SiteFooterLinks.jsx';

const FEATURE_ITEMS = [
  {
    title: 'Un diagnostic enfin concret',
    description: 'Transformez une impression floue en aperçu lisible de la répartition des tâches, de la charge mentale et de la pénibilité ressentie.',
    icon: FaChartLine
  },
  {
    title: 'Un cadre neutre pour dialoguer',
    description: 'Chacun répond de son côté, sans se consulter, pour faire émerger les écarts de perception sans se juger.',
    icon: FaBalanceScale
  },
  {
    title: 'Des résultats utiles rapidement',
    description: 'Dès qu’un premier thème est terminé, vous débloquez déjà des premiers résultats pour voir si cela vaut la peine d’aller plus loin.',
    icon: FaRegClock
  }
];

const STEP_ITEMS = [
  {
    number: '01',
    title: 'Créez votre foyer',
    description: 'En quelques clics, vous démarrez un espace commun et pouvez inviter votre partenaire avec un lien ou un code.'
  },
  {
    number: '02',
    title: 'Répondez chacun de votre côté',
    description: 'Le questionnaire est organisé par catégories pour avancer progressivement, même si vous n’avez que quelques minutes.'
  },
  {
    number: '03',
    title: 'Comparez et ouvrez la discussion',
    description: 'Visualisez les écarts, valorisez l’invisible et appuyez-vous sur une base factuelle pour en parler sereinement.'
  }
];

const USE_CASE_ITEMS = [
  {
    title: 'Vous sentez un déséquilibre',
    description: 'Mettez des mots et des chiffres sur ce qui est assumé au quotidien, y compris ce qui est mentalement porté.'
  },
  {
    title: 'Vous voulez prévenir les tensions',
    description: 'Utilisez l’outil avant qu’un conflit ne s’installe, pour repartir sur une base plus claire et plus juste.'
  },
  {
    title: 'Votre vie de foyer a changé',
    description: 'Arrivée d’un enfant, nouveau rythme pro, déménagement : le calculateur aide à refaire le point au bon moment.'
  }
];

const TESTIMONIAL_ITEMS = [
  {
    quote: 'On avait la sensation de faire “à peu près pareil”. Les résultats ont montré où se trouvaient vraiment les écarts.',
    name: 'Camille, 34 ans',
    context: 'En couple depuis 8 ans'
  },
  {
    quote: 'Le fait de répondre chacun de notre côté a évité les discussions défensives. On a pu parler des faits, pas des reproches.',
    name: 'Sarah, 29 ans',
    context: 'Jeune maman'
  },
  {
    quote: 'J’ai aimé pouvoir voir des premiers résultats sans devoir tout remplir d’un coup. Ça donne envie d’aller au bout.',
    name: 'Mehdi, 37 ans',
    context: 'Vie pro très chargée'
  }
];

const FAQ_ITEMS = [
  {
    question: 'Faut-il créer un compte pour commencer ?',
    answer: 'Non. Vous pouvez démarrer sans inscription. Un code secret suffit pour retrouver votre accès plus tard.'
  },
  {
    question: 'Combien de temps faut-il prévoir ?',
    answer: 'Le questionnaire est découpé par thèmes. Vous pouvez avancer par petites sessions et consulter de premiers résultats dès le premier thème terminé.'
  },
  {
    question: 'Est-ce que l’outil cherche à désigner un “coupable” ?',
    answer: 'Non. Le but est de rendre visible la réalité du foyer, d’ouvrir une discussion plus sereine et d’améliorer le bien-être de chacun·e.'
  },
  {
    question: 'Doit-on répondre ensemble ?',
    answer: 'Mieux vaut répondre chacun de son côté, sans se concerter. C’est justement ce qui permet d’identifier les écarts de perception.'
  }
];

function SectionHeader({ eyebrow, title, description, centered = true }) {
  return (
    <div className={centered ? 'max-w-3xl mx-auto text-center space-y-3' : 'space-y-3'}>
      {eyebrow ? (
        <div className="ptcm-section-pill">
          {eyebrow}
        </div>
      ) : null}
      <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-ptcm-ink dark:text-gray-100">
        {title}
      </h2>
      <p className="text-base leading-7 text-ptcm-muted dark:text-gray-300">
        {description}
      </p>
    </div>
  );
}

function FeatureCard({ title, description, icon: Icon }) {
  return (
    <Card className="h-full">
      <CardContent className="p-6 space-y-4">
        <div className="h-12 w-12 rounded-[20px] bg-ptcm-primary-soft dark:bg-blue-900/20 text-ptcm-primary dark:text-blue-300 flex items-center justify-center">
          <Icon className="h-5 w-5" />
        </div>
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-ptcm-ink dark:text-gray-100">{title}</h3>
          <p className="text-sm leading-6 text-ptcm-muted dark:text-gray-300">{description}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function StepCard({ number, title, description }) {
  return (
    <Card className="h-full">
      <CardContent className="p-6 space-y-4">
        <div className="text-sm font-semibold tracking-[0.22em] uppercase text-ptcm-primary dark:text-blue-400">
          {number}
        </div>
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-ptcm-ink dark:text-gray-100">{title}</h3>
          <p className="text-sm leading-6 text-ptcm-muted dark:text-gray-300">{description}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function UseCaseCard({ title, description }) {
  return (
    <Card className="h-full">
      <CardContent className="p-6 space-y-3">
        <div className="flex items-center gap-3 text-ptcm-ink dark:text-gray-100">
          <FaCheckCircle className="h-4 w-4 text-ptcm-primary dark:text-blue-400 shrink-0" />
          <h3 className="text-base font-semibold">{title}</h3>
        </div>
        <p className="text-sm leading-6 text-ptcm-muted dark:text-gray-300">{description}</p>
      </CardContent>
    </Card>
  );
}

function TestimonialCard({ quote, name, context }) {
  return (
    <Card className="h-full">
      <CardContent className="p-6 space-y-4">
        <p className="text-sm leading-6 text-ptcm-ink/90 dark:text-gray-200">
          “{quote}”
        </p>
        <div className="space-y-1">
          <div className="text-sm font-semibold text-ptcm-ink dark:text-gray-100">{name}</div>
          <div className="text-xs text-ptcm-muted dark:text-gray-400">{context}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function FaqItem({ question, answer }) {
  return (
    <details className="group rounded-[28px] border border-ptcm-border dark:border-gray-800 bg-white/92 dark:bg-gray-900 p-5 shadow-ptcm-card">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-left">
        <span className="text-base font-semibold text-ptcm-ink dark:text-gray-100">{question}</span>
        <span className="text-xl leading-none text-ptcm-muted transition-transform group-open:rotate-45">+</span>
      </summary>
      <p className="mt-4 text-sm leading-6 text-ptcm-muted dark:text-gray-300">{answer}</p>
    </details>
  );
}

export default function LoggedOutLandingPage() {
  const startSectionRef = useRef(null);
  const [started, setStarted] = useState(false);

  const scrollToStart = () => {
    scrollElementToTop(startSectionRef.current);
  };

  // When the user enters a sub-flow (create/join/reconnect), the marketing content is hidden;
  // bring the form to the top so it doesn't appear stranded mid-page.
  useEffect(() => {
    if (!started) return;
    const root = typeof document !== 'undefined' ? document.getElementById('app-scroll-root') : null;
    if (root) root.scrollTo({ top: 0, behavior: 'auto' });
  }, [started]);

  return (
    <div className={started ? 'max-w-3xl mx-auto px-3 sm:px-4 py-6 space-y-8' : 'max-w-6xl mx-auto px-3 sm:px-4 pb-24 space-y-16 sm:space-y-24'}>
      {!started && (
      <>
      <section className="pt-8 sm:pt-12">
        <div className="rounded-[38px] border border-ptcm-border dark:border-ptcm-border bg-[linear-gradient(145deg,rgba(241,227,221,0.92)_0%,rgba(255,255,255,0.96)_40%,rgba(247,226,218,0.84)_100%)] p-6 sm:p-10 shadow-ptcm-soft">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)] lg:items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 rounded-full border border-ptcm-border dark:border-ptcm-border bg-white/82 dark:bg-white/82 px-4 py-2 text-xs font-semibold text-ptcm-ink dark:text-ptcm-ink">
                <FaHeart className="h-3.5 w-3.5" />
                Un outil pour mieux comprendre la charge mentale dans le couple
              </div>

              <div className="space-y-4">
                <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-ptcm-ink dark:text-ptcm-ink">
                  Rendre visible ce qui pèse au quotidien pour en parler sereinement.
                </h1>
                <p className="max-w-2xl text-lg leading-8 text-ptcm-muted dark:text-ptcm-muted">
                  Paye ta charge mentale ! vous aide à objectiver la répartition des tâches, la charge mentale et la
                  pénibilité ressentie dans votre foyer, afin d’ouvrir un dialogue plus juste et plus apaisé.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button size="xl" onClick={scrollToStart}>
                  Tester gratuitement
                </Button>
                <Button size="xl" variant="outline" onClick={scrollToStart}>
                  J’ai un code d’invitation
                </Button>
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-[24px] border border-ptcm-border dark:border-ptcm-border bg-white/84 dark:bg-white/84 p-4">
                  <div className="text-sm font-semibold text-ptcm-ink dark:text-ptcm-ink">Sans inscription</div>
                  <div className="mt-1 text-xs text-ptcm-muted dark:text-ptcm-muted">Vous pouvez commencer tout de suite.</div>
                </div>
                <div className="rounded-[24px] border border-ptcm-border dark:border-ptcm-border bg-white/84 dark:bg-white/84 p-4">
                  <div className="text-sm font-semibold text-ptcm-ink dark:text-ptcm-ink">Premiers résultats rapides</div>
                  <div className="mt-1 text-xs text-ptcm-muted dark:text-ptcm-muted">Un seul thème terminé suffit pour débloquer des analyses.</div>
                </div>
                <div className="rounded-[24px] border border-ptcm-border dark:border-ptcm-border bg-white/84 dark:bg-white/84 p-4">
                  <div className="text-sm font-semibold text-ptcm-ink dark:text-ptcm-ink">Pensé pour le couple et la famille</div>
                  <div className="mt-1 text-xs text-ptcm-muted dark:text-ptcm-muted">Répondez chacun de votre côté, puis comparez.</div>
                </div>
              </div>
            </div>

            <Card className="overflow-hidden dark:!bg-white/92 dark:!border-ptcm-border dark:!text-ptcm-ink dark:shadow-ptcm-soft">
              <CardHeader className="space-y-3">
                <CardTitle className="text-xl">Pourquoi l’essayer ?</CardTitle>
                <CardDescription>
                  Pour passer du ressenti diffus à une base factuelle sur laquelle discuter.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3 rounded-[24px] bg-ptcm-panel/70 dark:bg-ptcm-panel/70 p-4">
                  <FaComments className="mt-1 h-4 w-4 text-ptcm-primary dark:text-ptcm-primary shrink-0" />
                  <div>
                    <div className="text-sm font-semibold text-ptcm-ink dark:text-ptcm-ink">Ouvrir le dialogue</div>
                    <div className="mt-1 text-sm text-ptcm-muted dark:text-ptcm-muted">
                      Mettre des mots sur les écarts de perception, sans tomber dans le reproche.
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3 rounded-[24px] bg-ptcm-panel/70 dark:bg-ptcm-panel/70 p-4">
                  <FaUserFriends className="mt-1 h-4 w-4 text-ptcm-primary dark:text-ptcm-primary shrink-0" />
                  <div>
                    <div className="text-sm font-semibold text-ptcm-ink dark:text-ptcm-ink">Valoriser l’invisible</div>
                    <div className="mt-1 text-sm text-ptcm-muted dark:text-ptcm-muted">
                      Montrer ce qui est fait, mais aussi ce qui est anticipé, pensé et porté mentalement.
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3 rounded-[24px] bg-ptcm-panel/70 dark:bg-ptcm-panel/70 p-4">
                  <FaBalanceScale className="mt-1 h-4 w-4 text-ptcm-primary dark:text-ptcm-primary shrink-0" />
                  <div>
                    <div className="text-sm font-semibold text-ptcm-ink dark:text-ptcm-ink">Agir plus justement</div>
                    <div className="mt-1 text-sm text-ptcm-muted dark:text-ptcm-muted">
                      Identifier ensemble les ajustements les plus utiles pour votre équilibre de foyer.
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="space-y-8">
        <SectionHeader
          eyebrow="Fonctionnalités phares"
          title="Un outil simple à prendre en main, mais vraiment utile une fois les réponses posées"
          description="Pensé pour aider les couples à aller au-delà du “j’ai l’impression de tout gérer” ou “je croyais en faire autant”."
        />

        <div className="grid gap-4 md:grid-cols-3">
          {FEATURE_ITEMS.map((item) => (
            <FeatureCard key={item.title} {...item} />
          ))}
        </div>
      </section>

      <section className="space-y-8">
        <SectionHeader
          eyebrow="Comment ça marche"
          title="Un parcours progressif, sans se perdre dans l’application"
          description="Vous entrez, vous contextualisez votre foyer, vous répondez par thèmes et vous obtenez des premiers enseignements rapidement."
        />

        <div className="grid gap-4 md:grid-cols-3">
          {STEP_ITEMS.map((item) => (
            <StepCard key={item.number} {...item} />
          ))}
        </div>

        <div className="flex justify-center">
          <Button size="lg" onClick={scrollToStart}>
            Commencer maintenant
          </Button>
        </div>
      </section>

      <section className="space-y-8">
        <SectionHeader
          eyebrow="Cas d’usage"
          title="Utile si vous sentez que quelque chose coince, même sans conflit ouvert"
          description="L’outil ne sert pas seulement à confirmer un déséquilibre. Il aide aussi à prévenir les tensions et à remettre de la clarté."
        />

        <div className="grid gap-4 md:grid-cols-3">
          {USE_CASE_ITEMS.map((item) => (
            <UseCaseCard key={item.title} {...item} />
          ))}
        </div>
      </section>

      <section className="space-y-8">
        <SectionHeader
          eyebrow="Avis"
          title="Des retours qui ressemblent à ce que vivent beaucoup de couples"
          description="Quelques exemples de retours entendus en beta test, lorsqu’on passe d’un ressenti vague à un support concret de discussion."
        />

        <div className="grid gap-4 lg:grid-cols-3">
          {TESTIMONIAL_ITEMS.map((item) => (
            <TestimonialCard key={item.name} {...item} />
          ))}
        </div>
      </section>

      <section className="space-y-8">
        <SectionHeader
          eyebrow="FAQ"
          title="Les questions qu’on se pose avant de tester"
          description="L’essentiel pour savoir à quoi vous attendre avant de vous lancer."
        />

        <div className="max-w-4xl mx-auto space-y-3">
          {FAQ_ITEMS.map((item) => (
            <FaqItem key={item.question} {...item} />
          ))}
        </div>
      </section>

      <section className="space-y-6">
        <Card className="overflow-hidden border-0 bg-[linear-gradient(135deg,rgba(31,38,73,1)_0%,rgba(109,99,219,1)_100%)] text-white shadow-ptcm-soft">
          <CardContent className="p-6 sm:p-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="space-y-2">
              <div className="text-sm font-semibold uppercase tracking-[0.18em] text-white/72">
                Prêt à essayer ?
              </div>
              <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">
                Faites un premier état des lieux de votre charge mentale de couple.
              </h2>
              <p className="text-sm sm:text-base text-white/84 max-w-2xl">
                Commencez seul·e, invitez votre partenaire ensuite, puis comparez vos vécus sur une base commune.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button size="lg" variant="secondary" onClick={scrollToStart}>
                Tester gratuitement
              </Button>
              <Button size="lg" className="bg-white/10 border border-white/20 hover:bg-white/20 text-white shadow-none" onClick={scrollToStart}>
                Rejoindre avec un code
              </Button>
            </div>
          </CardContent>
        </Card>
      </section>
      </>
      )}

      <section ref={startSectionRef} className="scroll-mt-6 space-y-6">
        {!started && (
          <SectionHeader
            eyebrow="Passer à l’action"
            title="Testez l’outil maintenant"
            description="Créez votre foyer ou rejoignez-en un avec un code d’invitation. Vous pourrez ensuite enchaîner directement sur le questionnaire."
          />
        )}

        <Onboarding embedded onActiveChange={setStarted} />
      </section>

      <section className="pt-2">
        <SiteFooterLinks centered className="justify-center" />
      </section>
    </div>
  );
}
